import { queryClient } from "../query-client";
import { queryKeys } from "../query-keys";
import { socialDraftsApi } from "../api";
import { projectsApi } from "../api/pro/projects";
import { getCachedProId, STALE_5MIN, STALE_1HR } from "./shared";

/**
 * Prefetch the social drafts list for the current pro.
 * Called by the `/social-studio` route loader.
 */
export function prefetchSocialDrafts() {
	const proId = getCachedProId();
	if (!proId) return;
	queryClient.ensureQueryData({
		queryKey: queryKeys.socialDrafts.list(proId),
		queryFn: () =>
			socialDraftsApi
				.list(proId)
				.then((r) => r.data ?? { drafts: [], pendingCount: 0 }),
		staleTime: 10_000,
	});
}

/**
 * Prefetch a single social draft by ID.
 * Called by the `/social-studio/$draftId` route loader.
 */
export function prefetchSocialDraft(draftId: string) {
	const proId = getCachedProId();
	if (!proId) return;
	queryClient.ensureQueryData({
		queryKey: queryKeys.socialDrafts.detail(proId, draftId),
		queryFn: () =>
			socialDraftsApi.get(proId, draftId).then((r) => r.data ?? null),
		staleTime: 10_000,
	});
}

/**
 * Prefetch data required by the create-reel page:
 * - Projects list with photo counts (for the photo selector)
 * - Music tracks (for MusicPicker)
 * - Templates (for TemplateChooser)
 */
export function prefetchCreateReel() {
	const proId = getCachedProId();
	if (!proId) return;
	queryClient.ensureQueryData({
		queryKey: [
			...queryKeys.projects.list(proId),
			"withPhotoCount",
		] as const,
		queryFn: () =>
			projectsApi
				.list(proId, undefined, { includePhotoCount: true })
				.then((r) => r.data || []),
		staleTime: STALE_5MIN,
	});
	queryClient.ensureQueryData({
		queryKey: queryKeys.socialDrafts.musicTracks(proId),
		queryFn: () =>
			socialDraftsApi
				.listMusicTracks(proId)
				.then((r) => r.data?.tracks ?? []),
		staleTime: STALE_1HR,
	});
	queryClient.ensureQueryData({
		queryKey: queryKeys.socialDrafts.templates(proId),
		queryFn: () =>
			socialDraftsApi
				.listTemplates(proId)
				.then((r) => r.data?.templates ?? []),
		staleTime: STALE_1HR,
	});
}
